import { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "next-i18next";
import { checkGeoroutingAvailability } from "@modules/user-data/services/checkGeoroutingAvailability";
import { OrderFields } from "@modules/order/constants";
import { useAuth } from "@modules/auth";
import useOrderData from "@modules/order/context/useOrderData";

export const useZipCodeValidation = (
  addressType: "from" | "to",
  fieldName: OrderFields,
  onRateLimitError?: () => void,
  onRateLimitCleared?: () => void,
  shouldValidateZipCode?: boolean,
) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { values, setFieldError, setFieldTouched, errors } = useFormikContext<any>();
  const lastValidatedZipRef = useRef<string>("");
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number>(0);
  const { setGeoroutingError, clearGeoroutingError, getGeoroutingErrors } = useOrderData();
  const [shouldValidate, setShouldValidate] = useState(false);

  const addressPrefix = addressType === "from" ? "SENDER" : "RECEIVER";
  const zipCode = values[OrderFields[`${addressPrefix}_ZIP_CODE`]];
  const street = values[OrderFields[`${addressPrefix}_STREET`]];
  const city = values[OrderFields[`${addressPrefix}_CITY`]];
  const country = addressType === "from"
    ? values[OrderFields.STARTING_COUNTRY]
    : values[OrderFields.DESTINATION_COUNTRY];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const validateGeorouting = async () => {
        if (!shouldValidateZipCode) {
          return;
        }

        if (!zipCode || !street || !city || !country) {
          return;
        }

        if (lastValidatedZipRef.current === zipCode) {
          return;
        }

        if (Date.now() < rateLimitedUntil) {
          onRateLimitError?.();

          return;
        }

        setShouldValidate(false);
        const errorMsg = t("order-message-georouting_not_available");

        try {
          const filteredOrder =
            isLoggedIn && !values[OrderFields.SHIPMENT_SIGNED_IN] && values[OrderFields.SHIPMENT_ID]
              ? { ...values, [OrderFields.SHIPMENT_ID]: null }
              : values;

          const response = await checkGeoroutingAvailability(addressType, filteredOrder);

          if (!response.isSuccess && response.statusCode === 429) {
            setRateLimitedUntil(Date.now() + 2 * 60 * 1000);
            onRateLimitError?.();
            setGeoroutingError(fieldName, t("address_fields-zip_code"));

            return;
          }

          if (response.isSuccess && response.data === 1) {
            setFieldError(fieldName, undefined);
            clearGeoroutingError(fieldName);
          } else {
            setFieldError(fieldName, errorMsg);
            setFieldTouched(fieldName, true, false);
            setGeoroutingError(fieldName, errorMsg);
          }

          lastValidatedZipRef.current = zipCode;
        } catch (err) {
          setFieldError(fieldName, errorMsg);
          setFieldTouched(fieldName, true, false);
          setGeoroutingError(fieldName, errorMsg);
        }
      };

      validateGeorouting();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [zipCode, shouldValidate]);

  useEffect(() => {
    const currentErrors = getGeoroutingErrors();
    const currentError = currentErrors[fieldName];

    if (currentError && zipCode && !errors[fieldName]) {
      setFieldError(fieldName, currentError);
    }
  }, [errors, getGeoroutingErrors, zipCode, fieldName, setFieldError]);

  useEffect(() => {
    if (Date.now() < rateLimitedUntil) {
      onRateLimitError?.();

      const intervalId = setInterval(() => {
        if (Date.now() >= rateLimitedUntil) {
          clearInterval(intervalId);
          onRateLimitCleared?.();

          if (zipCode && street && city && country) {
            lastValidatedZipRef.current = "";
            setShouldValidate(true);
          }
        } else {
          onRateLimitError?.();
        }
      }, 100);

      return () => clearInterval(intervalId);
    }
  }, [rateLimitedUntil, onRateLimitError, onRateLimitCleared]);

  return { };
};
